import React, { useMemo } from 'react';
import { Lock } from 'lucide-react';
import { ACHIEVEMENTS, RARITY_CONFIG, computeAllPlayersAchievements } from '../utils/achievementCalculations';
import { ICON_MAP } from './AchievementBadges';

export default function BadgeCollection({
  players,
  matches,
  firstAchievers,
  setSelectedPlayerId,
  setCurrentView,
}) {
  const badgeMap = useMemo(
    () => computeAllPlayersAchievements(players, matches),
    [players, matches]
  );

  // 희귀도순 정렬 (Epic → Rare → Normal)
  const sortedBadges = useMemo(() => {
    return [...ACHIEVEMENTS].sort((a, b) => {
      const aOrder = RARITY_CONFIG[a.rarity]?.sortOrder ?? 0;
      const bOrder = RARITY_CONFIG[b.rarity]?.sortOrder ?? 0;
      return bOrder - aOrder;
    });
  }, []);

  const earnedCount = Object.values(badgeMap).filter(v => v.players.length > 0).length;
  const totalEarnings = Object.values(badgeMap).reduce((sum, v) => sum + v.players.length, 0);

  const handlePlayerClick = (playerId) => {
    setSelectedPlayerId(playerId);
    setCurrentView('playerDetail');
  };

  return (
    <div className="space-y-4">
      {/* 헤더 */}
      <div className="bg-card rounded-lg border border-border p-3 sm:p-4">
        <div className="space-y-2">
          <h2 className="text-base font-semibold text-foreground">뱃지 도감</h2>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {ACHIEVEMENTS.length}종 중 {earnedCount}종 달성 · 총 {totalEarnings}회 획득
            </p>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {Object.entries(RARITY_CONFIG).map(([key, cfg]) => {
                if (key === 'legendary') return null;
                return (
                  <span key={key} className={`text-[9px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full border ${cfg.border} ${cfg.color} ${cfg.bgLabel}`}>
                    {cfg.label}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* 뱃지 카드 목록 */}
      <div className="grid gap-3">
        {sortedBadges.map(badge => {
          const data = badgeMap[badge.id];
          const earners = data?.players || [];
          const isEarned = earners.length > 0;
          const rarity = RARITY_CONFIG[badge.rarity];
          const Icon = ICON_MAP[badge.icon];
          const first = firstAchievers?.[badge.id];

          return (
            <div
              key={badge.id}
              className={`bg-card rounded-lg border-2 p-3 sm:p-4 transition-all ${
                isEarned
                  ? `${rarity?.border || 'border-border'}`
                  : 'border-border opacity-50'
              }`}
            >
              <div className="flex items-start gap-2 sm:gap-3">
                {/* 아이콘 */}
                <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center ${
                  isEarned ? badge.bgColor : 'bg-muted'
                }`}>
                  {isEarned && Icon ? (
                    <Icon className={`h-3.5 w-3.5 sm:h-5 sm:w-5 ${badge.color}`} />
                  ) : (
                    <Lock className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
                  )}
                </div>

                {/* 내용 */}
                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                    <span className={`text-xs sm:text-sm font-semibold truncate ${isEarned ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {isEarned ? badge.label : '???'}
                    </span>
                    <span className={`flex-shrink-0 text-[9px] sm:text-xs px-1.5 py-0.5 rounded-full border ${rarity?.border} ${rarity?.color} ${rarity?.bgLabel}`}>
                      {rarity?.label}
                    </span>
                  </div>
                  <p className="text-xs sm:text-xs text-muted-foreground mt-0.5">
                    {isEarned ? badge.description : '아직 아무도 달성하지 못했습니다'}
                  </p>

                  {isEarned && (
                    <div className="mt-2 sm:mt-2.5 space-y-1.5">
                      {/* 획득 인원 */}
                      <div className="flex flex-wrap items-center gap-1 sm:gap-1.5">
                        <span className="text-xs sm:text-xs text-muted-foreground">
                          획득 {earners.length}명:
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {earners.slice(0, 8).map(p => (
                            <button
                              key={p.id}
                              onClick={() => handlePlayerClick(p.id)}
                              className="text-xs sm:text-xs text-primary hover:underline"
                            >
                              {p.name}
                            </button>
                          ))}
                          {earners.length > 8 && (
                            <span className="text-xs sm:text-xs text-muted-foreground">
                              외 {earners.length - 8}명
                            </span>
                          )}
                        </div>
                      </div>

                      {/* 최초 획득자 */}
                      {first && (
                        <div className="flex flex-wrap items-center gap-1 sm:gap-1.5 pt-1.5 border-t border-border">
                          <span className="text-[9px] sm:text-xs text-primary/70">🏆 최초 달성:</span>
                          <button
                            disabled={!players.some(player => player.id === first.playerId)}
                            onClick={() => handlePlayerClick(first.playerId)}
                            className="text-[9px] sm:text-xs text-primary enabled:hover:underline disabled:cursor-default font-medium"
                          >
                            {first.playerName}
                          </button>
                          <span className="text-[9px] sm:text-xs text-muted-foreground">
                            {new Date(first.achievedAt).toLocaleDateString('ko-KR', {
                              year: '2-digit', month: 'numeric', day: 'numeric',
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 획득 수 */}
                <div className={`flex-shrink-0 w-8 sm:w-12 text-right ${isEarned ? '' : 'opacity-30'}`}>
                  <span className={`text-sm sm:text-lg font-bold ${isEarned ? badge.color : 'text-muted-foreground'}`}>
                    {earners.length}
                  </span>
                  <p className="text-[9px] sm:text-xs text-muted-foreground">명</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
